import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import {
  Box, Typography, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Button, Card, CardContent, useMediaQuery, Snackbar, Alert
} from '@mui/material';
import TablePagination from '@mui/material/TablePagination';
import {
  SupervisorAccount as ManagerAssignmentIcon
} from '@mui/icons-material';
import UserForm from '../components/UserForm';
import UsersImportDialog from '../components/UsersImportDialog';
import { useTheme } from '@mui/material/styles';
import { extractErrorMessage } from '../hooks/useErrorHandler';

const Users: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [userCount, setUserCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editUser, setEditUser] = useState<any | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importSummaryOpen, setImportSummaryOpen] = useState(false);
  const [importSummaryMsg, setImportSummaryMsg] = useState('');
  const [importSummarySeverity, setImportSummarySeverity] = useState<'success'|'warning'|'error'>('success');
  const [showArchived, setShowArchived] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const fetchUsers = () => {
    setLoading(true);
    const params = new URLSearchParams();
    params.append('page', String(page + 1));
    params.append('page_size', String(rowsPerPage));
    if (showArchived) {
      params.append('include_archived', 'true');
    }
    api.get(`/users/?${params.toString()}`)
      .then(response => {
        const results = response.data.results || response.data;
        setUsers(results);
        setUserCount(response.data.count ?? results.length ?? 0);
        setLoading(false);
      })
      .catch(error => {
        setError('Failed to fetch users');
        setLoading(false);
        console.error('Fetch users error:', error);
      });
  };

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, showArchived]);

  const handleAdd = () => {
    setEditUser(null);
    setFormOpen(true);
  };

  const handleEdit = (user: any) => {
    setEditUser(user);
    setFormOpen(true);
  };

  const handleFormClose = () => {
    setFormOpen(false);
    setEditUser(null);
    setError(null); // Clear any errors when closing the form
  };

  const handleFormSubmit = async (data: any) => {
    try {
      if (editUser) {
        await api.put(`/users/${editUser.id}/`, data);
      } else {
        await api.post('/users/', data);
      }
      fetchUsers();
      setFormOpen(false);
      setEditUser(null);
    } catch (error) {
      const errorMessage = extractErrorMessage(error);
      setError(errorMessage || (editUser ? 'Failed to update user. Please check your input and try again.' : 'Failed to create user. Please check your input and try again.'));
      console.error(editUser ? 'Update user error:' : 'Create user error:', error);
      throw error;
    }
  };

  const handleArchive = async (userId: number) => {
    setActionLoading(userId);
    try {
      await api.post(`/users/${userId}/archive/`);
      setImportSummaryMsg('User archived successfully');
      setImportSummarySeverity('success');
      setImportSummaryOpen(true);
      fetchUsers();
    } catch (error) {
      setError('Failed to archive user');
      console.error('Archive user error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnarchive = async (userId: number) => {
    setActionLoading(userId);
    try {
      await api.post(`/users/${userId}/unarchive/`);
      setImportSummaryMsg('User unarchived successfully');
      setImportSummarySeverity('success');
      setImportSummaryOpen(true);
      fetchUsers();
    } catch (error) {
      setError('Failed to unarchive user');
      console.error('Unarchive user error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResendInvitation = async (userId: number) => {
    setActionLoading(userId);
    try {
      await api.post(`/users/${userId}/resend_invitation/`);
      setImportSummaryMsg('Invitation email resent successfully');
      setImportSummarySeverity('success');
      setImportSummaryOpen(true);
      fetchUsers(); // Refresh to update invitation status
    } catch (error: any) {
      const errorMessage = extractErrorMessage(error);
      setError(errorMessage || 'Failed to resend invitation email');
      console.error('Resend invitation error:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedUsers.length === 0) return;
    
    setBulkActionLoading(true);
    try {
      const promises = selectedUsers.map(userId => api.post(`/users/${userId}/archive/`));
      await Promise.all(promises);
      setImportSummaryMsg(`${selectedUsers.length} users archived successfully`);
      setImportSummarySeverity('success');
      setImportSummaryOpen(true);
      setSelectedUsers([]);
      fetchUsers();
    } catch (error) {
      setError('Failed to archive users');
      console.error('Bulk archive error:', error);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleSelectUser = (userId: number) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers.length === users.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users.map(user => user.id));
    }
  };

  if (loading) return <CircularProgress />;
  // Error display is now handled by Snackbar below

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, mb: 3, gap: 2 }}>
        <Typography variant="h4" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>Users</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
          <Button
            variant={showArchived ? "contained" : "outlined"}
            onClick={() => {
              setShowArchived(!showArchived);
              setPage(0); // Reset to first page when toggling
            }}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            {showArchived ? 'Hide Archived' : 'Show Archived'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ManagerAssignmentIcon />}
            onClick={() => navigate('/manager-assignment')}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Manager Assignment
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => setImportOpen(true)}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Import Users
          </Button>
          {selectedUsers.length > 0 && !showArchived && (
            <Button 
              variant="outlined" 
              color="error" 
              onClick={handleBulkArchive}
              disabled={bulkActionLoading}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {bulkActionLoading ? <CircularProgress size={16} /> : `Archive ${selectedUsers.length} Users`}
            </Button>
          )}
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleAdd}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Add User
          </Button>
        </Box>
      </Box>
      {isMobile ? (
        <Box>
          {users.map((user: any) => (
            <Card key={user.id} sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ wordBreak: 'break-word' }}>
                  {user.email} ({user.role})
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Name:</strong> {user.first_name} {user.last_name}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Department:</strong> {user.department?.name || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Location:</strong> {user.location?.name || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Hire Date:</strong> {user.hire_date || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Invitation Status:</strong>{' '}
                  {user.invitation_status === 'pending' ? (
                    <Box component="span" sx={{ color: 'warning.main', fontWeight: 'medium' }}>
                      Pending Invitation
                    </Box>
                  ) : user.invitation_status === 'expired' ? (
                    <Box component="span" sx={{ color: 'error.main', fontWeight: 'medium' }}>
                      Invitation Expired
                    </Box>
                  ) : (
                    <Box component="span" sx={{ color: 'success.main', fontWeight: 'medium' }}>
                      Accepted
                    </Box>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Employment Status:</strong> {user.employment_status || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Time Zone:</strong> {user.timezone || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Phone:</strong> {user.phone_number || 'N/A'}
                </Typography>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                  <strong>Active:</strong> {user.is_active ? (
                    <Box component="span" sx={{ color: 'success.main', fontWeight: 'medium' }}>Yes</Box>
                  ) : (
                    <Box component="span" sx={{ color: 'text.secondary' }}>No</Box>
                  )}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  <strong>Archived:</strong>{' '}
                  {user.archived ? (
                    <Box component="span" sx={{ color: 'error.main', fontWeight: 'medium' }}>Yes</Box>
                  ) : (
                    <Box component="span" sx={{ color: 'success.main', fontWeight: 'medium' }}>No</Box>
                  )}
                </Typography>
                <Button variant="outlined" size="small" onClick={() => handleEdit(user)} fullWidth>
                  Edit
                </Button>
              </CardContent>
            </Card>
          ))}
          <TablePagination
            component="div"
            count={userCount}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Box>
      ) : (
        <>
        <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
          <Table size="small" sx={{ width: '100%' }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <input
                    type="checkbox"
                    checked={selectedUsers.length === users.length && users.length > 0}
                    onChange={handleSelectAll}
                    disabled={showArchived}
                  />
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Email</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>First Name</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Last Name</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Role</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Invitation Status</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>Manager</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Department</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', lg: 'table-cell' } }}>Location</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', xl: 'table-cell' } }}>Hire Date</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', xl: 'table-cell' } }}>Status</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', xl: 'table-cell' } }}>Active</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', display: { xs: 'none', xl: 'table-cell' } }}>Archived</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user: any) => (
                <TableRow key={user.id}>
                  <TableCell padding="checkbox">
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleSelectUser(user.id)}
                      disabled={showArchived || user.archived}
                    />
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>{user.first_name}</TableCell>
                  <TableCell>{user.last_name}</TableCell>
                  <TableCell>{user.role}</TableCell>
                  <TableCell>
                    {user.invitation_status === 'pending' ? (
                      <Box sx={{ color: 'warning.main', fontWeight: 'medium' }}>
                        Pending Invitation
                      </Box>
                    ) : user.invitation_status === 'expired' ? (
                      <Box sx={{ color: 'error.main', fontWeight: 'medium' }}>
                        Invitation Expired
                      </Box>
                    ) : (
                      <Box sx={{ color: 'success.main', fontWeight: 'medium' }}>
                        Accepted
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{user.primary_manager || (Array.isArray(user.managers) && user.managers[0]) || '—'}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{user.department?.name || 'N/A'}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>{user.location?.name || 'N/A'}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>{user.hire_date}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>{user.employment_status}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>{user.is_active ? 'Yes' : 'No'}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', xl: 'table-cell' } }}>
                    {user.archived ? (
                      <Box sx={{ color: 'error.main', fontWeight: 'medium' }}>
                        Yes
                      </Box>
                    ) : (
                      <Box sx={{ color: 'success.main', fontWeight: 'medium' }}>
                        No
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button variant="outlined" size="small" onClick={() => handleEdit(user)}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={userCount}
          page={page}
          onPageChange={(_e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
        </>
      )}
      <UserForm
        open={formOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        initialData={editUser}
        onArchive={handleArchive}
        onUnarchive={handleUnarchive}
        onResendInvitation={handleResendInvitation}
      />
      <UsersImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={async (rows) => {
          try {
            const normalizeDate = (v?: string | null) => {
              if (!v) return null;
              const d = new Date(v);
              if (Number.isNaN(d.getTime())) return null;
              return d.toISOString().slice(0, 10);
            };
            // Preload department and location maps by name (case-insensitive)
            let locationNameToId: Record<string, number> = {};
            let departmentNameToId: Record<string, number> = {};
            try {
              const [lr, dr] = await Promise.all([
                api.get('/locations/'),
                api.get('/departments/'),
              ]);
              const locs = lr.data.results || lr.data || [];
              const depts = dr.data.results || dr.data || [];
              locationNameToId = (Array.isArray(locs) ? locs : []).reduce((acc: any, loc: any) => {
                if (loc && loc.name) acc[String(loc.name).trim().toLowerCase()] = loc.id;
                return acc;
              }, {} as Record<string, number>);
              departmentNameToId = (Array.isArray(depts) ? depts : []).reduce((acc: any, d: any) => {
                if (d && d.name) acc[String(d.name).trim().toLowerCase()] = d.id;
                return acc;
              }, {} as Record<string, number>);
            } catch {}
            let created = 0;
            let skipped = 0;
            const failures: { email: string; reason: string }[] = [];

            // Basic sequential creation; continue on duplicates and collect summary
            for (const row of rows) {
              const payload: any = {
                email: row.email,
                first_name: row.first_name,
                last_name: row.last_name,
                role: row.role || 'employee',
                employment_status: row.employment_status || 'active',
                hire_date: normalizeDate(row.hire_date || null),
                phone_number: row.phone_number || '',
                timezone: row.timezone || 'America/Los_Angeles',
                employee_id: row.employee_id || null,
                is_active: true,
              };
              // Map location name to id if provided
              if (row.location) {
                const key = String(row.location).trim().toLowerCase();
                const locId = locationNameToId[key];
                if (locId) payload.location_id = locId;
              }
              // Map department name to id if provided
              if (row.department) {
                const dkey = String(row.department).trim().toLowerCase();
                const deptId = departmentNameToId[dkey];
                if (deptId) payload.department_id = deptId;
              }
              try {
                await api.post('/users/', payload);
                created += 1;
              } catch (e: any) {
                const data = e?.response?.data || {};
                const duplicate = (data.email && String(data.email).toLowerCase().includes('already exists'))
                  || (data.employee_id && String(data.employee_id).toLowerCase().includes('already exists'))
                  || e?.response?.status === 409;
                if (duplicate) {
                  skipped += 1;
                  continue;
                }
                failures.push({ email: row.email, reason: JSON.stringify(data) || String(e) });
              }
            }
            setImportOpen(false);
            fetchUsers();
            // Prepare a friendly summary
            const parts: string[] = [];
            parts.push(`Created ${created}`);
            if (skipped > 0) parts.push(`skipped ${skipped} duplicate${skipped>1?'s':''}`);
            if (failures.length > 0) parts.push(`failed ${failures.length}`);
            const summary = `Import complete: ${parts.join(', ')}.`;
            setImportSummaryMsg(summary);
            setImportSummarySeverity(failures.length > 0 ? 'error' : skipped > 0 ? 'warning' : 'success');
            setImportSummaryOpen(true);
          } catch (e) {
            console.error('User import failed', e);
            setImportOpen(false);
            fetchUsers();
          }
        }}
      />

      {/* Error snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Import summary snackbar */}
      <Snackbar
        open={importSummaryOpen}
        autoHideDuration={6000}
        onClose={() => setImportSummaryOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setImportSummaryOpen(false)} severity={importSummarySeverity} sx={{ width: '100%' }}>
          {importSummaryMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users; 